import { Box } from '@looker/components';
import { DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import type { ICustomFilter } from '../../../../../../helpers/helpers';
import type { Filter } from '../../../../../../stores/filters';
import { useFilterUIStore } from '../../../../../../stores/filterUI';
import { useSubGroupFilterState } from '../../../hooks/useSubGroupFilterState';
import { IncludeExcludeFilter } from '../IncludeExcludeFilter';
import { ContentArea, LayoutContainer, LeftNav, NavItem } from './styles';

interface ProfessionAndSpecialityProps extends ICustomFilter {
  professionFilterName: string;
  specialtyFilterName: string;
}

type FilterMap = Record<string, Filter>;

const ProfessionAndSpeciality: FC<ProfessionAndSpecialityProps> = ({
  onChange,
  onExpressionTypeChange,
  professionFilterName,
  specialtyFilterName,
}) => {
  const openFilterName = useFilterUIStore((state) => state.openFilterName);

  const [activeCategory, setActiveCategory] = useState<'profession' | 'specialty'>(
    openFilterName === specialtyFilterName ? 'specialty' : 'profession',
  );
  const [profMap, setProfMap] = useState<FilterMap>({});
  const [specMap, setSpecMap] = useState<FilterMap>({});

  const setDialogFitContentWidth = useFilterUIStore((state) => state.setDialogFitContentWidth);

  const emitMerged = useCallback(() => {
    onChange?.({ ...profMap, ...specMap });
  }, [onChange, profMap, specMap]);

  const { handleChange: handleProfessionChange } = useSubGroupFilterState(professionFilterName, (f) => setProfMap(f));

  const { handleChange: handleSpecialtyChange } = useSubGroupFilterState(specialtyFilterName, (f) => setSpecMap(f));

  useEffect(() => {
    emitMerged();
  }, [emitMerged]);

  useEffect(() => {
    setDialogFitContentWidth(true);
    return () => setDialogFitContentWidth(false);
  }, [setDialogFitContentWidth]);

  const profCount = useMemo(() => {
    const filter = profMap[professionFilterName];
    if (!filter?.expression) return 0;
    return filter.expression.split(',').length;
  }, [profMap, professionFilterName]);

  const specCount = useMemo(() => {
    const filter = specMap[specialtyFilterName];
    if (!filter?.expression) return 0;
    return filter.expression.split(',').length;
  }, [specMap, specialtyFilterName]);

  const isProfessionActive = activeCategory === 'profession';

  return (
    <LayoutContainer>
      <LeftNav gap="u2">
        <NavItem active={isProfessionActive} onClick={() => setActiveCategory('profession')} between>
          <DsTypography weight={isProfessionActive ? 'bold' : 'normal'} color="inherit">
            Profession
          </DsTypography>
          <DsTypography color="inherit" display="inline" variant="caption">
            {profCount}
          </DsTypography>
        </NavItem>

        <NavItem active={!isProfessionActive} onClick={() => setActiveCategory('specialty')} between>
          <DsTypography weight={!isProfessionActive ? 'bold' : 'normal'} color="inherit">
            Specialty
          </DsTypography>
          <DsTypography color="inherit" display="inline" variant="caption">
            {specCount}
          </DsTypography>
        </NavItem>
      </LeftNav>

      <ContentArea>
        <Box display={isProfessionActive ? 'block' : 'none'} height="100%">
          <IncludeExcludeFilter
            filterName={professionFilterName}
            label="Professions"
            onMultiFilterChange={handleProfessionChange}
            onExpressionTypeChange={onExpressionTypeChange}
            selectedOnlyLabel="Selected Only"
          />
        </Box>
        <Box display={!isProfessionActive ? 'block' : 'none'} height="100%">
          <IncludeExcludeFilter
            filterName={specialtyFilterName}
            label="Specialties"
            onMultiFilterChange={handleSpecialtyChange}
            onExpressionTypeChange={onExpressionTypeChange}
            selectedOnlyLabel="Selected Only"
          />
        </Box>
      </ContentArea>
    </LayoutContainer>
  );
};

export default memo(ProfessionAndSpeciality);
